import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Chart from 'chart.js';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Form,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle
} from 'reactstrap';

import { buttonStyle } from 'layouts/LoginAutoAuth/components/utils';
import { useForm } from 'react-hook-form';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from 'hooks/useMemorizeInputsFilters';
import ReactLoader from 'components/Loader';
import { chartOptions, parseOptions } from 'variables/charts.js';

import { useSelector } from 'react-redux';
import CRMDashboardService from '../../service';
import { toast } from 'react-toastify';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { toISOFormat, firstDayOfMonth } from '../../shared/dataFormater';
import DefaultDashboardFieldsFilterForm from './form/DefaultFieldsFilterForm';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import { parseIntFromString } from 'utils/fuctions/parseIntFromString';
import GraphicAccountsWithDateByResponsible from './GraphicAccountsWithDateByResponsible';
import FormTooltip from 'components/Inputs/FormTooltip';
import AccountsStatusTable from './AccountsStatusTable';
import GraphicAccountsStatus from './GraphicAccountsStatus';

if (window.Chart) {
  parseOptions(Chart, chartOptions());
}
const AccountDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [showNoResponsible, setShowNoResponsible] = useState(false);
  const { control, handleSubmit, setValue, getValues, register, reset } =
    useForm();
  const { getMemorizedFilters, memorizeFilters } = useMemorizeFilters(
    POSSIBLE_FILTERS_ENTITIES.CRM_ACCOUNT_DASHBOARD
  );
  const [
    accountsCountByResponsibleWithDate,
    setAccountsCountByResponsibleWithDate
  ] = useState(null);
  const [defaultRecordType, setDefaultRecordType] = useState(null);
  const [selectedRecordTypeId, setSelectedRecordTypeId] = useState(null);
  const {
    recordTypes: accountRecordTypes,
    getRecordTypesByUser: getAccountRecordTypesByUser
  } = useRecordTypesByUser(entitiesName.ACCOUNT);
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.ACCOUNT,
    defaultRecordType?.value || defaultRecordType?.id
  );

  const filteredAccounts = useMemo(() => {
    if (!accountsCountByResponsibleWithDate) return [];
    return accountsCountByResponsibleWithDate.filter((item) =>
      showNoResponsible ? true : !!item.responsibleName
    );
  }, [accountsCountByResponsibleWithDate, showNoResponsible]);

  const handleSelectRecordType = useCallback(
    (recordTypeId) => {
      const found = accountRecordTypes.find((rt) => rt.id === recordTypeId);
      setDefaultRecordType(found);
      setSelectedRecordTypeId(recordTypeId);
    },
    [accountRecordTypes]
  );
  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    setLoading(true);

    if (defaultRecordType) {
      const filters = getMemorizedFilters();
      const formatedData =
        filters && Object.keys(filters).length > 0
          ? {
              ...filters,
              recordType: defaultRecordType.value || defaultRecordType.id
            }
          : {
              dateBegin: `${toISOFormat(firstDayOfMonth)}`,
              dateEnd: `${toISOFormat(new Date().toLocaleDateString('pt-br'))}`,
              recordType: defaultRecordType.value || defaultRecordType.id
            };

      CRMDashboardService.getAccountsCountByStageWithDate(formatedData)
        .then((response) => {
          const listCustomFields = response?.data?.data;
          setAccountsCountByResponsibleWithDate(
            listCustomFields?.accountsCountByResponsible
          );
        })
        .catch((error) => {
          toast.error(error || 'Ops, ocorreu um erro!', {
            autoClose: 3000,
            closeOnClick: true
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [defaultRecordType]);

  useEffect(() => {
    getAccountRecordTypesByUser();
  }, [getAccountRecordTypesByUser]);

  useEffect(() => {
    if (accountRecordTypes !== null) {
      const filters = getMemorizedFilters();
      let found = null;
      let selectedId = null;
      if (filters && filters.selectedRecordTypeId) {
        found = accountRecordTypes.find(
          (rt) => rt.id === filters.selectedRecordTypeId
        );
        selectedId = filters.selectedRecordTypeId;
      }
      if (!found) {
        found = accountRecordTypes[0] || null;
        selectedId = found ? found.id : null;
      }
      if (found) {
        setDefaultRecordType(found);
        setSelectedRecordTypeId(selectedId);
      }
    }
  }, [accountRecordTypes]);

  useEffect(() => {
    const filters = getMemorizedFilters();
    if (filters) {
      reset(filters);
      if (filters.selectedRecordTypeId) {
        setSelectedRecordTypeId(filters.selectedRecordTypeId);
      }
    }
  }, []);

  if (loading) {
    return (
      <Row className="justify-content-md-center mt-4">
        <ReactLoader />
      </Row>
    );
  }
  const onSubmit = (data) => {
    try {
      const fullData = {
        ...data,
        showNoResponsible,
        selectedRecordTypeId:
          selectedRecordTypeId || (defaultRecordType && defaultRecordType.id)
      };
      memorizeFilters(fullData);
      let formatedData = {
        ...fullData,
        dateBegin: fullData.dateBegin,
        dateEnd: fullData.dateEnd,
        recordType: fullData.selectedRecordTypeId || ''
      };
      if (customFields) {
        customFields.forEach((field) => {
          if (field.type === customFieldTypes.LIST) {
            const possibleValues = field.options.map((value) => value.value);
            if (
              possibleValues.includes(fullData[field.name]) &&
              fullData[field.name].length > 0
            ) {
              formatedData[field.name] = fullData[field.name];
            }
          }
        });
      }

      const currencyFieldsName =
        customFields &&
        customFields
          .filter((field) => field.type === customFieldTypes.CURRENCY)
          .map((field) => field.name);

      formatedData.recordType = defaultRecordType.id || defaultRecordType.value;

      Object.keys(fullData).forEach((key) => {
        if (currencyFieldsName.includes(key)) {
          if (fullData[key]) {
            formatedData[key] = parseIntFromString(fullData[key]);
          }
        }
      });

      CRMDashboardService.getAccountsCountByStageWithDate(formatedData)
        .then((response) => {
          setAccountsCountByResponsibleWithDate(
            response?.data?.data?.accountsCountByResponsible
          );
        })
        .catch((error) => {
          toast.error(error || 'Ops, ocorreu um erro!', {
            autoClose: 3000,
            closeOnClick: true
          });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      toast.error(err || 'Ops, ocorreu um erro!', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const SelectRecordTypeDropDown = ({
    recordTypes = [],
    selectedRecordTypeName = '',
    handleSelectRecordType
  }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen((prevState) => !prevState);

    return (
      <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle
          tag="span"
          className="selected-record-type-name"
          data-toggle="dropdown"
          aria-expanded={dropdownOpen}
        >
          {selectedRecordTypeName}
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem header>Selecione</DropdownItem>
          {recordTypes.map((rt) => (
            <DropdownItem
              key={rt.id}
              onClick={() => handleSelectRecordType(rt.id)}
            >
              {rt.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  return (
    <Container fluid className="pt-4">
      <Card>
        <CardBody>
          {accountRecordTypes ? (
            <div>
              <div className="small">
                <span className="font-weight-bold">Tipo de registro: </span>
                <SelectRecordTypeDropDown
                  recordTypes={accountRecordTypes}
                  selectedRecordTypeName={
                    accountRecordTypes.find(
                      (rt) => rt.id === selectedRecordTypeId
                    )?.name ||
                    defaultRecordType?.label ||
                    defaultRecordType?.name ||
                    ''
                  }
                  handleSelectRecordType={handleSelectRecordType}
                />
              </div>
              <Form
                className="needs-validation"
                onSubmit={handleSubmit(onSubmit)}
              >
                <DefaultDashboardFieldsFilterForm
                  setValue={setValue}
                  control={control}
                  getValues={getValues}
                  recordTypeId={defaultRecordType.value || defaultRecordType.id}
                  register={register}
                  showNoResponsible={showNoResponsible}
                  setShowNoResponsible={setShowNoResponsible}
                  reset={reset}
                  handleSubmit={handleSubmit}
                  onSubmit={onSubmit}
                />

                <Button
                  type="submit"
                  style={buttonStyle(systemConfiguration.primary_color)}
                  className="mb-2"
                >
                  Buscar
                </Button>
              </Form>
              <Row>
                <Col>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Contas ativas{' '}
                        <FormTooltip
                          text={
                            `Ativas: Contas que realizaram compras, alcançando 100%.` +
                            `\n` +
                            `Inativas: Contas que não compraram ou não atingiram 100%.`
                          }
                        />
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {filteredAccounts &&
                      filteredAccounts.length > 0 &&
                      filteredAccounts.some(
                        (item) =>
                          Number(item.activeCount) > 0 ||
                          Number(item.inactiveCount) > 0
                      ) ? (
                        <GraphicAccountsStatus
                          accountsCountBySeller={filteredAccounts}
                        />
                      ) : (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: '#888'
                          }}
                        >
                          Nenhum resultado encontrado para os filtros
                          selecionados.
                        </div>
                      )}
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Status das contas por Responsável{' '}
                        <FormTooltip
                          text={
                            `Ativas: Contas que realizaram compras, alcançando 100%.` +
                            `\n` +
                            `Inativas: Contas que não compraram ou não atingiram 100%.`
                          }
                        />
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {filteredAccounts &&
                      filteredAccounts.length > 0 &&
                      filteredAccounts.some(
                        (item) =>
                          Number(item.activeCount) > 0 ||
                          Number(item.inactiveCount) > 0
                      ) ? (
                        <GraphicAccountsWithDateByResponsible
                          accountsCountBySeller={filteredAccounts}
                        />
                      ) : (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: '#888'
                          }}
                        >
                          Nenhum resultado encontrado para os filtros
                          selecionados.
                        </div>
                      )}
                    </CardBody>
                  </Card>
                  <Card>
                    <CardHeader>
                      <h6 className="surtitle">Visão Geral</h6>
                      <h5 className="h3 mb-0">
                        Tabela de Contas por Responsável com Indicadores{' '}
                        <FormTooltip
                          text={
                            `Ativas: Contas que realizaram compras, alcançando 100%.` +
                            `\n` +
                            `Inativas: Contas que não compraram ou não atingiram 100%.`
                          }
                        />
                      </h5>
                    </CardHeader>
                    <CardBody
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(1, 1fr)',
                        justifyContent: 'center'
                      }}
                    >
                      {filteredAccounts &&
                      filteredAccounts.length > 0 &&
                      filteredAccounts.some(
                        (item) =>
                          Number(item.activeCount) > 0 ||
                          Number(item.inactiveCount) > 0
                      ) ? (
                        <AccountsStatusTable
                          accountsCountBySeller={filteredAccounts}
                        />
                      ) : (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '2rem',
                            color: '#888'
                          }}
                        >
                          Nenhum resultado encontrado para os filtros
                          selecionados.
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          ) : (
            <div>
              <span className="small">
                Você não tem acesso a nenhum tipo de Conta.
              </span>
            </div>
          )}
        </CardBody>
      </Card>
    </Container>
  );
};

export default AccountDashboard;
